import { useState, useEffect } from "react";
import {
  Resource,
  Create,
  SimpleForm,
  TextInput,
  useNotify,
  required,
  Confirm,
} from "react-admin";
import { Card, CardContent, Button, CircularProgress } from "@material-ui/core";
import { useTokenOps } from "../components/TokenContext";
import { hostname } from "../providers/api";
import { useHistory } from "react-router-dom";
import { naReasonFor } from "./ecobalyse/gtinList";

// Validación de GTIN
const validateGtin = (value) => {
  if (!value) return "GTIN is required";
  const gtinRegex = /^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/;
  if (!gtinRegex.test(value)) {
    return "GTIN must be 8, 12, 13, or 14 digits";
  }
  return undefined;
};

// Vista Show del GTIN
const AccountArticleGtinShow = (props) => {
  const [gtin, setGtin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const tokenOps = useTokenOps();
  const notify = useNotify();
  const history = useHistory();

  const fetchGtin = () => {
    const gtinId = props.id;
    if (!gtinId) return;

    setLoading(true);
    console.log('[GTIN Show] Fetching GTIN ID:', gtinId);

    // Usar el endpoint getAllGtin que incluye name, reference, projectName, etc.
    fetch(`${hostname}/ecobalyse/getAllGtin`, {
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error fetching GTINs: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const gtinArray = Array.isArray(data) ? data : data.data || [];

        // Buscar el GTIN específico por ID
        const foundGtin = gtinArray.find(g => g.id === gtinId);

        if (!foundGtin) {
          throw new Error(`GTIN not found with ID: ${gtinId}`);
        }

        console.log('[GTIN Show] Received data:', foundGtin);
        setGtin(foundGtin);
      })
      .catch((error) => {
        console.error('[GTIN Show] Error:', error);
        console.error('[GTIN Show] GTIN ID that failed:', gtinId);
        notify(`Error: ${error.message}`, "error");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchGtin();
  }, [props.id]);

  const handleDeleteClick = () => {
    setConfirmDeleteOpen(true);
  };

  const handleDeleteConfirm = () => {
    setConfirmDeleteOpen(false);
    setDeleting(true);

    fetch(`${hostname}/account-article-gtin/${props.id}`, {
      method: 'DELETE',
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error deleting GTIN: ${response.status}`);
        }
        notify('GTIN deleted successfully', 'success');
        // Redirigir a la tabla de GTINs (tab GTIN List)
        history.push('/ecobalyse/main/show#gtin-list');
        // Forzar recarga después de un pequeño delay para asegurar navegación
        setTimeout(() => {
          window.location.href = '/#/ecobalyse/main/show';
        }, 100);
      })
      .catch((error) => {
        console.error('[GTIN Delete] Error:', error);
        notify(`Error: ${error.message}`, "error");
      })
      .finally(() => setDeleting(false));
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteOpen(false);
  };

  if (loading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "400px"
      }}>
        <CircularProgress />
      </div>
    );
  }

  if (!gtin) {
    return (
      <Card style={{ marginTop: '1em', padding: '20px' }}>
        <CardContent>
          <h2>GTIN not found</h2>
          <Button
            variant="contained"
            color="primary"
            onClick={() => history.push('/ecobalyse/main/show')}
            style={{ marginTop: '20px' }}
          >
            Back to GTIN List
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Función para formatear fecha ISO a DD/MM/YYYY (igual que en la lista)
  const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    try {
      const date = new Date(isoDate);
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "-";
    }
  };

  return (
    <Card style={{ marginTop: '1em' }}>
      <CardContent>
        <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2>GTIN Details</h2>
          <div style={{ display: "flex", gap: "10px" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push(`/account-article-gtin/${props.id}`)}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              onClick={handleDeleteClick}
              disabled={deleting}
              style={{
                backgroundColor: deleting ? "#999" : "#f44336",
                color: "white"
              }}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
            <Button
              variant="outlined"
              color="default"
              onClick={() => history.push('/ecobalyse/main/show')}
            >
              Back to List
            </Button>
          </div>
        </div>

        <div style={{ display: "grid", gap: "15px" }}>
          <div>
            <strong>Name:</strong>
            <div style={{ marginTop: "5px", color: "#666" }}>{gtin.name || "-"}</div>
          </div>

          <div>
            <strong>Article ID:</strong>
            <div style={{ marginTop: "5px", color: "#666" }}>{gtin.articleId || "-"}</div>
          </div>

          <div>
            <strong>Reference:</strong>
            <div style={{ marginTop: "5px", color: "#666" }}>{gtin.reference || "-"}</div>
          </div>

          <div>
            <strong>GTIN:</strong>
            <div style={{ marginTop: "5px", color: "#666" }}>{gtin.gtin || "-"}</div>
          </div>

          <div>
            <strong>Project:</strong>
            <div style={{ marginTop: "5px", color: "#666" }}>{gtin.projectName || "-"}</div>
          </div>

          <div>
            <strong>Traceability:</strong>
            <div style={{ marginTop: "5px", color: "#666" }}>{gtin.traceabilityStatus || "-"}</div>
          </div>

          <div>
            <strong>Score:</strong>
            <div style={{ marginTop: "5px", color: "#666" }}>{gtin.EcobalyseStatus || "-"}</div>
          </div>

          <div>
            <strong>N/A reason:</strong>
            <div style={{ marginTop: "5px", color: "#666", overflowWrap: "anywhere" }}>
              {naReasonFor(gtin) || "-"}
            </div>
          </div>

          <div>
            <strong>Last Calculation:</strong>
            <div style={{ marginTop: "5px", color: "#666" }}>{formatDate(gtin.lastEcobalyseAt)}</div>
          </div>

          <div>
            <strong>Version:</strong>
            <div style={{ marginTop: "5px", color: "#666" }}>{gtin.ecobalyseVersion || "-"}</div>
          </div>
        </div>
      </CardContent>

      <Confirm
        isOpen={confirmDeleteOpen}
        title="Eliminar GTIN"
        content="¿Está seguro que desea eliminar este GTIN? Esta acción no se puede deshacer."
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />
    </Card>
  );
};

// Vista Create
const AccountArticleGtinCreate = (props) => {
  const history = useHistory();

  const onSuccess = (data) => {
    // Redirigir a la vista show del GTIN creado
    history.push(`/account-article-gtin/${data.id}/show`);
  };

  return (
    <Create {...props} title="Create New GTIN" onSuccess={onSuccess}>
      <SimpleForm>
        <TextInput
          source="articleReference"
          label="Article Reference"
          validate={[required()]}
          helperText="Enter the Article Reference"
          fullWidth
        />
        <TextInput
          source="gtin"
          label="GTIN"
          validate={[required(), validateGtin]}
          helperText="Enter GTIN (8, 12, 13, or 14 digits)"
          fullWidth
        />
      </SimpleForm>
    </Create>
  );
};

// Vista Edit
const AccountArticleGtinEdit = (props) => {
  const [gtinData, setGtinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gtinValue, setGtinValue] = useState('');
  const tokenOps = useTokenOps();
  const notify = useNotify();
  const history = useHistory();

  const fetchGtin = () => {
    const gtinId = props.id;
    if (!gtinId) return;

    setLoading(true);
    console.log('[GTIN Edit] Fetching GTIN ID:', gtinId);

    // Usar el endpoint getAllGtin que incluye name, reference, etc.
    fetch(`${hostname}/ecobalyse/getAllGtin`, {
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error fetching GTINs: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const gtinArray = Array.isArray(data) ? data : data.data || [];

        // Buscar el GTIN específico por ID
        const foundGtin = gtinArray.find(g => g.id === gtinId);

        if (!foundGtin) {
          throw new Error(`GTIN not found with ID: ${gtinId}`);
        }

        console.log('[GTIN Edit] Received data:', foundGtin);
        setGtinData(foundGtin);
        setGtinValue(foundGtin.gtin || '');
      })
      .catch((error) => {
        console.error('[GTIN Edit] Error:', error);
        console.error('[GTIN Edit] GTIN ID that failed:', gtinId);
        notify(`Error: ${error.message}`, "error");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchGtin();
  }, [props.id]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const gtinValidation = validateGtin(gtinValue);
    if (gtinValidation) {
      notify(gtinValidation, 'warning');
      return;
    }

    setSaving(true);

    // Solo enviamos el GTIN para actualizar
    fetch(`${hostname}/account-article-gtin/${props.id}`, {
      method: 'PATCH',
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ gtin: gtinValue }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error updating GTIN: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        notify('GTIN updated successfully', 'success');
        history.push(`/account-article-gtin/${data.id}/show`);
      })
      .catch((error) => {
        console.error('[GTIN Edit] Error:', error);
        notify(`Error: ${error.message}`, 'error');
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "400px"
      }}>
        <CircularProgress />
      </div>
    );
  }

  if (!gtinData) {
    return (
      <Card style={{ marginTop: '1em', padding: '20px' }}>
        <CardContent>
          <h2>GTIN not found</h2>
          <Button
            variant="contained"
            color="primary"
            onClick={() => history.push('/ecobalyse/main/show')}
            style={{ marginTop: '20px' }}
          >
            Back to GTIN List
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card style={{ marginTop: '1em' }}>
      <CardContent>
        <h2>Edit GTIN</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#666' }}>
              Article ID
            </label>
            <input
              type="text"
              value={gtinData.articleId || ''}
              disabled
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#f5f5f5',
                color: '#666',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#666' }}>
              Name
            </label>
            <input
              type="text"
              value={gtinData.name || ''}
              disabled
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#f5f5f5',
                color: '#666',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#666' }}>
              Reference
            </label>
            <input
              type="text"
              value={gtinData.reference || ''}
              disabled
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#f5f5f5',
                color: '#666',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              GTIN *
            </label>
            <input
              type="text"
              value={gtinValue}
              onChange={(e) => setGtinValue(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              placeholder="Enter GTIN (8, 12, 13, or 14 digits)"
              disabled={saving}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '30px' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => history.push(`/account-article-gtin/${props.id}/show`)}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

const accountArticleGtinResource = () => (
  <Resource
    name="account-article-gtin"
    show={AccountArticleGtinShow}
    create={AccountArticleGtinCreate}
    edit={AccountArticleGtinEdit}
  />
);

export default accountArticleGtinResource;
